/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnChanges,
  OnDestroy,
  signal,
  SimpleChanges,
  viewChild,
} from '@angular/core';
import * as Plotly from 'plotly.js-dist-min';
import { Config, Data, Layout } from 'plotly.js-dist-min';
import { fromEvent, debounceTime, Subject, takeUntil } from 'rxjs';
import {
  ExperimentData,
  IDevice,
  IFlask,
  ITimeValue,
} from 'src/app/_models/experiment-data';
import {
  MatSlideToggle,
  MatSlideToggleChange,
} from '@angular/material/slide-toggle';
import { FormBuilder, Validators } from '@angular/forms';
import { FormBase } from 'src/app/_models/form-base';
import { UnderlinedDropdownFormFieldComponent } from '../../forms/underlined-dropdown-form-field/underlined-dropdown-form-field.component';
import { ButtonComponent } from '../../buttons/button/button.component';
import { UnderlinedFormFieldComponent } from '../../forms/underlined-form-field/underlined-form-field.component';
import { Device } from 'src/app/_models/device';
import { MatIconButtonComponent } from '../../buttons/mat-icon-button/mat-icon-button.component';
import { AdvancedSettings } from 'src/app/_models/advanced-settings';

@Component({
  selector: 'app-plotly-graph',
  templateUrl: './plotly-graph.component.html',
  styleUrls: ['./plotly-graph.component.scss'],
  imports: [
    MatSlideToggle,
    UnderlinedDropdownFormFieldComponent,
    ButtonComponent,
    UnderlinedFormFieldComponent,
    MatIconButtonComponent,
  ],
})
export class PlotlyGraphComponent implements AfterViewInit, OnChanges, OnDestroy {
  readonly plotlyDiv = viewChild<ElementRef>('plotlyDiv');

  private readonly destroy$ = new Subject<void>();
  protected formBuilder = inject(FormBuilder);

  // Colour scale - same as D3 version
  private readonly coloursList: string[][] = [
    ['#6ECF9C', '#6C73C1', '#CE517E', '#37494A'],
    ['#F03E2B', '#BE4FC1', '#CB9F50', '#7FCE4C'],
    ['#5BB4E9', '#FFA62A', '#969696', '#D6A7D7'],
    ['#346684', '#496A35', '#553056', '#AA6E5C'],
  ];

  checked = signal(false);
  dragMode = signal<'zoom' | 'pan'>('zoom');
  graphMode = signal<'markers' | 'lines+markers'>('markers');
  private graphReady = false;
  errorMessage: string | null = null;
  private readonly logMin = 0.0001;
  private readonly margin = {
    left: 70,
    right: 5,
    top: 10,
    bottom: 60,
  };

  private width = 800;
  private height = 450;
  private readonly maxHeight = 450;

  // Point magnification settings (matching D3 behavior)
  private readonly minMarkerSize = 2;
  private readonly maxMarkerSize = 12;
  private baseMarkerSize = 6; // Set dynamically based on dataset size
  private initialXRange: [number, number] | null = null;
  private initialYRange: [number, number] | null = null;

  dataToRender: IRenderData[] = [];
  private labelList: IPropertyColour[] = [];
  legendData = signal<ILegendDevice[]>([]);

  // Form variables
  minGraphHeight = 200;
  minGraphWidth = 200;
  advancedSettings: AdvancedSettings;

  // Signal inputs
  readonly graphId = input('plotly-scatter');
  readonly yAxisLabel = input('Y Axis Label');
  readonly graphType = input<'OD' | 'Temp' | 'pH'>();
  readonly experimentData = input<ExperimentData>();
  readonly isLogarithmic = input(false);
  readonly devices = input<Device[]>();
  readonly experimentName = input<string>();

  private previousExperimentData: ExperimentData | undefined;

  widthField = computed(() =>
    new FormBase({
      key: `${this.graphId()}-width`,
      label: 'Width (px)',
      type: 'number',
      placeholder: '',
      disabled: false,
      required: false,
      value: '',
      options: [],
    }),
  );

  heightField = computed(() =>
    new FormBase({
      key: `${this.graphId()}-height`,
      label: 'Height (px)',
      type: 'number',
      placeholder: '',
      disabled: false,
      required: false,
      value: '',
      options: [],
    }),
  );

  yMinRangeField = computed(() =>
    new FormBase({
      key: `${this.graphId()}-yMinRange`,
      label: 'y Min',
      type: 'number',
      placeholder: '',
      disabled: false,
      required: false,
      value: '',
      options: [],
    }),
  );

  yMaxRangeField = computed(() =>
    new FormBase({
      key: `${this.graphId()}-yMaxRange`,
      label: 'y Max',
      type: 'number',
      placeholder: '',
      disabled: false,
      required: false,
      value: '',
      options: [],
    }),
  );

  xMinRangeField = computed(() =>
    new FormBase({
      key: `${this.graphId()}-xMinRange`,
      label: 'x Min',
      type: 'number',
      placeholder: '',
      disabled: false,
      required: false,
      value: '',
      options: [],
    }),
  );

  xMaxRangeField = computed(() =>
    new FormBase({
      key: `${this.graphId()}-xMaxRange`,
      label: 'x Max',
      type: 'number',
      placeholder: '',
      disabled: false,
      required: false,
      value: '',
      options: [],
    }),
  );

  ratioField = computed(
    () =>
      new FormBase({
        key: `${this.graphId()}-ratio`,
        label: 'Aspect Ratio',
        type: 'dropdown',
        placeholder: '',
        disabled: false,
        required: false,
        value: 'default',
        options: [
          { key: 'default', value: '1.8:1' },
          { key: 'square', value: '1:1' },
          { key: 'threeByTwo', value: '3:2' },
        ],
      }),
  );

  advancedSettingsForm = computed(() =>
    this.formBuilder.group({
      [this.widthField().key]: [
        this.widthField().value,
        Validators.compose([
          Validators.required,
          Validators.min(this.minGraphWidth),
        ]),
      ],
      [this.heightField().key]: [
        this.heightField().value,
        Validators.compose([
          Validators.required,
          Validators.min(this.minGraphHeight),
        ]),
      ],
      [this.yMinRangeField().key]: [this.yMinRangeField().value, Validators.required],
      [this.yMaxRangeField().key]: [
        this.yMaxRangeField().value,
        [Validators.required],
      ],
      [this.xMinRangeField().key]: [
        this.xMinRangeField().value,
        [Validators.required],
      ],
      [this.xMaxRangeField().key]: [
        this.xMaxRangeField().value,
        [Validators.required],
      ],
      [this.ratioField().key]: [this.ratioField().value],
    }),
  );

  constructor() {
    // Effect to react to experimentData changes (replaces ngOnChanges)
    effect(() => {
      const data = this.experimentData();
      if (
        data &&
        this.previousExperimentData &&
        data !== this.previousExperimentData
      ) {
        this.dataToRender = [];
        this.labelList = [];
        this.parseData(data, true);
      }
      this.previousExperimentData = data;
    });
  }

  ngAfterViewInit(): void {
    this.initialiseGraph();
  }

  private readonly initialiseGraph = (): void => {
    const data = this.experimentData();
    if (data) {
      this.parseData(data);
    }

    // Resize observer
    const resizeObservable$ = fromEvent(window, 'resize').pipe(
      debounceTime(300),
    );

    resizeObservable$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.handleResize();
    });

    // Form value changes
    this.advancedSettingsForm()
      .get(this.ratioField().key)
      ?.valueChanges.subscribe((value) => {
        this.updateDimensions(value);
      });

    this.advancedSettingsForm()
      .get(this.xMinRangeField().key)
      ?.valueChanges.subscribe((value) => {
        this.advancedSettingsForm().controls[
          this.xMaxRangeField().key
        ].setValidators([Validators.required, Validators.min(value)]);
        this.advancedSettingsForm().controls[
          this.xMaxRangeField().key
        ].updateValueAndValidity({
          onlySelf: true,
          emitEvent: false,
        });
      });

    this.advancedSettingsForm()
      .get(this.yMinRangeField().key)
      ?.valueChanges.subscribe((value) => {
        this.advancedSettingsForm().controls[
          this.yMaxRangeField().key
        ].setValidators([Validators.required, Validators.min(value)]);
        this.advancedSettingsForm().controls[
          this.yMaxRangeField().key
        ].updateValueAndValidity({
          onlySelf: true,
          emitEvent: false,
        });
      });
  };

  ngOnChanges(simpleChanges: SimpleChanges): void {
    if (
      simpleChanges['experimentData'] &&
      !simpleChanges['experimentData'].firstChange
    ) {
      this.dataToRender = [];
      this.labelList = [];
      this.parseData(this.experimentData(), true);
    }
  }

  /**
   * Safely execute Plotly operations with error handling and user feedback
   */
  private safePlotlyOperation(operation: () => void, errorMsg: string): void {
    if (!this.graphReady || !this.plotlyDiv()?.nativeElement) {
      return;
    }
    try {
      operation();
    } catch (error) {
      this.errorMessage = errorMsg;
    }
  }

  /**
   * Calculate zoom scale and update marker sizes (matching D3 point magnification)
   */
  private updateMarkerSizeOnZoom(): void {
    if (!this.graphReady || !this.plotlyDiv()?.nativeElement || !this.initialXRange || !this.initialYRange) {
      return;
    }

    const layout = this.plotlyDiv().nativeElement.layout;
    const currentXRange = layout.xaxis?.range;
    const currentYRange = layout.yaxis?.range;

    if (!currentXRange || !currentYRange) return;

    // Calculate zoom scale based on how much the axis range has shrunk
    const initialXSpan = this.initialXRange[1] - this.initialXRange[0];
    const initialYSpan = this.initialYRange[1] - this.initialYRange[0];
    const currentXSpan = currentXRange[1] - currentXRange[0];
    const currentYSpan = currentYRange[1] - currentYRange[0];

    // Use the average of X and Y zoom scales
    const xZoomScale = initialXSpan / currentXSpan;
    const yZoomScale = initialYSpan / currentYSpan;
    const zoomScale = (xZoomScale + yZoomScale) / 2;

    // Calculate new marker size: scale linearly with zoom but clamp to min/max
    const scaledSize = this.baseMarkerSize * zoomScale;
    const newSize = Math.min(
      Math.max(this.minMarkerSize, scaledSize),
      this.maxMarkerSize
    );

    // Update all traces with the new marker size
    const traceCount = this.plotlyDiv().nativeElement.data?.length || 0;
    if (traceCount > 0) {
      const traceIndices = Array.from({ length: traceCount }, (_, i) => i);
      try {
        Plotly.restyle(this.plotlyDiv().nativeElement, { 'marker.size': newSize }, traceIndices);
      } catch (error) {
        // Silently fail - marker size update is not critical
      }
    }
  }

  /**
   * Clear any active tooltips/hover state
   */
  private clearTooltips(): void {
    if (this.plotlyDiv()?.nativeElement) {
      try {
        Plotly.Fx.hover(this.plotlyDiv().nativeElement, []);
      } catch (error) {
        // Silently fail - tooltip clearing is not critical
      }
    }
  }

  toggleChange(event: MatSlideToggleChange): void {
    this.checked.set(event.checked);

    // Guard: don't proceed if graph isn't initialized yet
    if (!this.graphReady) {
      return;
    }

    const currentLayout = this.plotlyDiv()?.nativeElement?.layout;

    // Preserve the current axis range from the graph (in case user has zoomed)
    const currentXRange = currentLayout.xaxis?.range;
    const currentYRange = currentLayout.yaxis?.range;

    const update: Partial<Layout> = {
      xaxis: {
        ...this.getXAxisConfig(),
        range: currentXRange,
        fixedrange: this.checked(),
      },
      yaxis: {
        ...this.getYAxisConfig(),
        range: currentYRange,
        fixedrange: this.checked(),
      },
    };

    this.safePlotlyOperation(
      () => Plotly.relayout(this.plotlyDiv().nativeElement, update),
      'Failed to toggle zoom. Try again'
    );
  }

  resetZoom(): void {
    // Use efficient loop instead of spread operator for large datasets
    const { minY, maxY, minX, maxX } = this.getDataBounds();

    const xVal = (maxX - minX) * 0.05;
    const yVal = (maxY - minY) * 0.05;

    const totalPoints = this.dataToRender.length;
    const useWebGL = totalPoints > 5000;

    this.advancedSettingsForm().patchValue({
      [this.widthField().key]: this.advancedSettings.width.toString(),
      [this.heightField().key]: this.advancedSettings.height.toString(),
      [this.yMinRangeField().key]: this.advancedSettings.yMin.toString(),
      [this.yMaxRangeField().key]: this.advancedSettings.yMax.toString(),
      [this.xMinRangeField().key]: this.advancedSettings.xMin.toString(),
      [this.xMaxRangeField().key]: this.advancedSettings.xMax.toString(),
      [this.ratioField().key]: this.advancedSettings.aspectRatio,
    });

    const update: Partial<Layout> = {
      'xaxis.range': [minX - xVal, maxX + xVal],
      'yaxis.range': this.isLogarithmic()
        ? [this.logMin, maxY * 1.1]
        : [minY - yVal, maxY + yVal],
    };

    // WebGL (scattergl) doesn't animate smoothly - points "pop" while axes animate
    // So only animate for SVG (scatter) datasets
    if (useWebGL) {
      // Instant reset for WebGL datasets
      this.safePlotlyOperation(
        () => {
          Plotly.relayout(this.plotlyDiv().nativeElement, update);
          this.clearTooltips();
        },
        'Failed to reset zoom. Please try again.'
      );
    } else {
      // Smooth animation for SVG datasets (< 5k points)
      this.safePlotlyOperation(
        () => {
          Plotly.animate(
            this.plotlyDiv().nativeElement,
            { layout: update },
            {
              transition: {
                duration: 750,
                easing: 'cubic-in-out',
              },
              frame: {
                duration: 750,
              },
            },
          );
          this.clearTooltips();
        },
        'Failed to reset zoom. Please try again.'
      );
    }
  }

  zoomIn(): void {
    if (!this.graphReady || !this.plotlyDiv()?.nativeElement) return;

    const layout = this.plotlyDiv().nativeElement.layout;
    const xRange = layout.xaxis.range;
    const yRange = layout.yaxis.range;

    const xCenter = (xRange[0] + xRange[1]) / 2;
    const yCenter = (yRange[0] + yRange[1]) / 2;
    const xHalf = (xRange[1] - xRange[0]) / 4;
    const yHalf = (yRange[1] - yRange[0]) / 4;

    this.safePlotlyOperation(
      () => {
        Plotly.relayout(this.plotlyDiv().nativeElement, {
          'xaxis.range': [xCenter - xHalf, xCenter + xHalf],
          'yaxis.range': [yCenter - yHalf, yCenter + yHalf],
        });
        this.clearTooltips();
      },
      'Failed to zoom in. Please try again.'
    );
  }

  zoomOut(): void {
    if (!this.graphReady || !this.plotlyDiv()?.nativeElement) return;

    const layout = this.plotlyDiv().nativeElement.layout;
    const xRange = layout.xaxis.range;
    const yRange = layout.yaxis.range;

    const xCenter = (xRange[0] + xRange[1]) / 2;
    const yCenter = (yRange[0] + yRange[1]) / 2;
    const xHalf = xRange[1] - xRange[0];
    const yHalf = yRange[1] - yRange[0];

    this.safePlotlyOperation(
      () => {
        Plotly.relayout(this.plotlyDiv().nativeElement, {
          'xaxis.range': [xCenter - xHalf, xCenter + xHalf],
          'yaxis.range': [yCenter - yHalf, yCenter + yHalf],
        });
        this.clearTooltips();
      },
      'Failed to zoom out. Please try again.'
    );
  }

  setDragMode(mode: 'zoom' | 'pan'): void {
    this.dragMode.set(mode);
    this.safePlotlyOperation(
      () => Plotly.relayout(this.plotlyDiv().nativeElement, { dragmode: mode }),
      'Failed to change drag mode. Please try again.'
    );
  }

  setGraphMode(mode: 'markers' | 'lines+markers'): void {
    this.graphMode.set(mode);

    if (!this.graphReady || !this.plotlyDiv()?.nativeElement) return;

    const traceCount = this.plotlyDiv().nativeElement.data?.length || 0;
    if (traceCount > 0) {
      const traceIndices = Array.from({ length: traceCount }, (_, i) => i);
      this.safePlotlyOperation(
        () => Plotly.restyle(this.plotlyDiv().nativeElement, { mode }, traceIndices),
        'Failed to change graph mode. Please try again.'
      );
    }
  }

  downloadImage(): void {
    this.safePlotlyOperation(
      () => Plotly.downloadImage(this.plotlyDiv().nativeElement, {
        format: 'png',
        filename: `${this.experimentName()} - ${this.graphType()}`,
        width: this.width + this.margin.left + this.margin.right,
        height: this.height + this.margin.top + this.margin.bottom,
      }),
      'Failed to download image. Please try again.'
    );
  }

  resizeGraph(): void {
    const width = parseInt(this.advancedSettingsForm().controls[this.widthField().key]?.value);
    const height = parseInt(
      this.advancedSettingsForm().controls[this.heightField().key]?.value,
    );
    const xMin = parseFloat(
      this.advancedSettingsForm().controls[this.xMinRangeField().key]?.value,
    );
    const xMax = parseFloat(
      this.advancedSettingsForm().controls[this.xMaxRangeField().key]?.value,
    );
    const yMin = parseFloat(
      this.advancedSettingsForm().controls[this.yMinRangeField().key]?.value,
    );
    const yMax = parseFloat(
      this.advancedSettingsForm().controls[this.yMaxRangeField().key]?.value,
    );

    // Validate all parsed values
    if (isNaN(width) || isNaN(height) || isNaN(xMin) || isNaN(xMax) || isNaN(yMin) || isNaN(yMax)) {
      this.errorMessage = 'Invalid dimensions provided. Please check all input values are valid numbers.';
      return;
    }

    // Validate logical constraints
    if (width <= 0 || height <= 0) {
      this.errorMessage = 'Width and height must be greater than zero.';
      return;
    }

    if (xMin >= xMax) {
      this.errorMessage = 'X-axis minimum must be less than maximum.';
      return;
    }

    if (yMin >= yMax) {
      this.errorMessage = 'Y-axis minimum must be less than maximum.';
      return;
    }

    // Calculate padding (5% of range, same as D3)
    const xVal = (xMax - xMin) * 0.05;
    const yVal = (yMax - yMin) * 0.05;

    // Store the plot area dimensions
    this.width = width;
    this.height = height;

    this.safePlotlyOperation(
      () => {
        Plotly.relayout(this.plotlyDiv().nativeElement, {
          // Plotly width/height include margins
          width: width + this.margin.left + this.margin.right,
          height: height + this.margin.top + this.margin.bottom,
          'xaxis.range': [xMin - xVal, xMax + xVal],
          'yaxis.range': this.isLogarithmic()
            ? [Math.log10(this.logMin), Math.log10(yMax * 1.1)]
            : [yMin - yVal, yMax + yVal],
        });
        this.clearTooltips();
      },
      'Failed to resize graph. Please check your dimensions and try again.'
    );
  }

  updateSizeFormValues(): void {
    this.advancedSettingsForm().patchValue({
      [this.widthField().key]: this.width.toFixed(2).toString(),
      [this.heightField().key]: this.height.toFixed(2).toString(),
    });
  }

  private handleResize(): void {
    const value =
      this.advancedSettingsForm().controls[this.ratioField().key]?.value || 'default';
    this.updateDimensions(value);
  }

  private updateDimensions(ratio?: string): void {
    // Use setTimeout to ensure browser has completed layout
    // This is more reliable than requestAnimationFrame for form-triggered updates
    setTimeout(() => {
      if (!this.plotlyDiv()?.nativeElement) return;

      // Use getBoundingClientRect for more reliable width measurement
      const rect = this.plotlyDiv()!.nativeElement.getBoundingClientRect();
      const containerWidth = rect.width > 0 ? rect.width : 800;

      const maxWidth = containerWidth - (this.margin.left + this.margin.right);

      if (!ratio || ratio === 'default') {
        // Default: full width, height based on 1.8:1 but capped at maxHeight
        // This matches the initial load behavior
        this.width = maxWidth;
        const desiredHeight = maxWidth / 1.8;
        this.height =
          desiredHeight < this.maxHeight ? desiredHeight : this.maxHeight;
      } else if (ratio === 'square') {
        // 1:1 ratio - true square constrained by maxHeight
        const size = Math.min(maxWidth, this.maxHeight);
        this.width = size;
        this.height = size;
      } else if (ratio === 'threeByTwo') {
        // 3:2 ratio (1.5:1) - maintains exact ratio
        const desiredHeight = maxWidth / 1.5;
        if (desiredHeight <= this.maxHeight) {
          this.width = maxWidth;
          this.height = desiredHeight;
        } else {
          this.height = this.maxHeight;
          this.width = this.maxHeight * 1.5;
        }
      }

      this.updateSizeFormValues();

      const newLayout = {
        width: this.width + this.margin.left + this.margin.right,
        height: this.height + this.margin.top + this.margin.bottom,
      };

      this.safePlotlyOperation(
        () => {
          Plotly.relayout(this.plotlyDiv().nativeElement, newLayout);
          this.clearTooltips();
        },
        'Failed to update graph dimensions. Please try again.'
      );
    }, 0);
  }

  private readonly parseData = (
    experimentData: ExperimentData,
    isUpdate = false,
  ): void => {
    const property = experimentData.dataType;
    const deviceList: IDevice[] = experimentData.devices;

    deviceList?.forEach((dataSet, index) => {
      const validFlasks = dataSet.flasks.filter(
        (flask) => flask && Object.keys(flask).length > 0 && flask.name,
      );

      validFlasks.forEach((flask: IFlask, flaskIndex) => {
        const deviceFlaskProperty = `${dataSet.deviceId}_${flask.name}_${property}`;

        if (
          flask.values &&
          Array.isArray(flask.values) &&
          flask.values.length > 0
        ) {
          flask.values.forEach((timeValue: ITimeValue) => {
            if (
              timeValue &&
              typeof timeValue.time === 'number' &&
              typeof timeValue.value === 'number'
            ) {
              const y = timeValue.value;
              const x = timeValue.time;
              const tOD = timeValue.targetOD || null;

              this.dataToRender.push({
                DeviceFlaskProperty: deviceFlaskProperty,
                Time: x,
                Value: y,
                DeviceName: dataSet.deviceId.toString(),
                FlaskName: `${this.graphType()} ${flask.name.slice(-1)}`,
                TargetOD: tOD,
              });
            }
          });

          this.labelList.push({
            deviceFlaskProperty,
            colour: this.coloursList[index][flaskIndex],
          });
        }
      });

      if (experimentData.devices.length === index + 1) {
        if (isUpdate) {
          this.updateGraph();
        } else {
          this.drawGraph();
        }
      }
    });
  };

  /**
   * Efficiently calculate min/max bounds without spread operators
   * to avoid call stack size exceeded errors with large datasets
   */
  private getDataBounds(): {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
  } {
    if (this.dataToRender.length === 0) {
      return { minX: 0, maxX: 1, minY: 0, maxY: 1 };
    }

    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;

    for (let i = 0; i < this.dataToRender.length; i++) {
      const point = this.dataToRender[i];
      if (point.Time < minX) minX = point.Time;
      if (point.Time > maxX) maxX = point.Time;
      if (point.Value < minY) minY = point.Value;
      if (point.Value > maxY) maxY = point.Value;
    }

    return { minX, maxX, minY, maxY };
  }

  private getXAxisConfig(): Partial<Layout['xaxis']> {
    const bounds = this.getDataBounds();
    const minX =
      parseFloat(this.advancedSettingsForm().controls[this.xMinRangeField().key]?.value) ||
      bounds.minX;
    const maxX =
      parseFloat(this.advancedSettingsForm().controls[this.xMaxRangeField().key]?.value) ||
      bounds.maxX;
    const xVal = (maxX - minX) * 0.05;

    return {
      title: {
        text: 'Time [h]',
        font: {
          weight: 600,
          family: 'figtree',
          size: 18,
        },
      },
      range: [minX - xVal, maxX + xVal],
      fixedrange: this.checked(),
      showgrid: true,
      zeroline: false,
      nticks: 10, // Suggest ~10 ticks for X-axis too
      tickmode: 'auto',
    };
  }

  private getYAxisConfig(): Partial<Layout['yaxis']> {
    const bounds = this.getDataBounds();
    const minY =
      parseFloat(this.advancedSettingsForm().controls[this.yMinRangeField().key]?.value) ||
      bounds.minY;
    const maxY =
      parseFloat(this.advancedSettingsForm().controls[this.yMaxRangeField().key]?.value) ||
      bounds.maxY;
    const yVal = (maxY - minY) * 0.05;

    return {
      title: {
        text: this.yAxisLabel(),
        font: {
          weight: 600,
          family: 'figtree',
          size: 18,
        },
      },
      type: this.isLogarithmic() ? 'log' : 'linear',
      range: this.isLogarithmic()
        ? [Math.log10(this.logMin), Math.log10(maxY * 1.1)]
        : [minY - yVal, maxY + yVal],
      fixedrange: this.checked(),
      showgrid: true,
      zeroline: false,
      nticks: 10, // Suggest ~10 ticks (Plotly will adjust based on nice numbers)
      tickmode: 'auto', // Auto-generate nice tick values
    };
  }

  private readonly drawGraph = (): void => {
    // Use efficient loop instead of spread operator for large datasets
    const { minY, maxY, minX, maxX } = this.getDataBounds();

    const roundedMinY = parseFloat(minY.toFixed(4)) || 0;
    let roundedMaxY = parseFloat(maxY.toFixed(4)) || 1;
    const roundedMinX = parseFloat(minX.toFixed(4)) || 0;
    let roundedMaxX = parseFloat(maxX.toFixed(4)) || 1;

    if (roundedMinX === roundedMaxX) {
      roundedMaxX = roundedMinX + 1;
    }
    if (roundedMinY === roundedMaxY) {
      roundedMaxY = roundedMinY + 1;
    }

    this.advancedSettingsForm().patchValue({
      [this.xMinRangeField().key]: roundedMinX.toString(),
      [this.xMaxRangeField().key]: roundedMaxX.toString(),
      [this.yMinRangeField().key]: roundedMinY.toString(),
      [this.yMaxRangeField().key]: roundedMaxY.toString(),
    });

    // Calculate dimensions
    const containerWidth = this.plotlyDiv()?.nativeElement?.offsetWidth || 800;
    this.width = containerWidth - (this.margin.left + this.margin.right);
    const currentHeight = this.width / 1.8;
    this.height =
      currentHeight < this.maxHeight ? currentHeight : this.maxHeight;
    this.updateSizeFormValues();

    // Set default form values
    this.advancedSettings = new AdvancedSettings(
      roundedMinX,
      roundedMaxX,
      roundedMinY,
      roundedMaxY,
      parseInt(this.height.toFixed(2)),
      parseInt(this.width.toFixed(2)),
      this.ratioField().value,
    );

    // Group data by DeviceFlaskProperty
    const groupedData = new Map<string, IRenderData[]>();
    this.dataToRender.forEach((point) => {
      if (!groupedData.has(point.DeviceFlaskProperty)) {
        groupedData.set(point.DeviceFlaskProperty, []);
      }
      groupedData.get(point.DeviceFlaskProperty)!.push(point);
    });

    // Determine if we should use WebGL based on total point count
    const totalPoints = this.dataToRender.length;
    const useWebGL = totalPoints > 5000; // Threshold: 5k points
    const traceType = useWebGL ? 'scattergl' : 'scatter';

    // Optimise marker size for very large datasets and store for zoom scaling
    const markerSize = totalPoints > 50000 ? 3 : totalPoints > 10000 ? 4 : 6;
    this.baseMarkerSize = markerSize;

    // Create traces and build custom legend data
    const seenDevices = new Set<string>();
    const traces: Data[] = [];
    const legendItems: ILegendDevice[] = [];
    const deviceMap = new Map<string, ILegendDevice>();

    Array.from(groupedData.entries()).forEach(([key, points]) => {
      const label = this.labelList.find((l) => l.deviceFlaskProperty === key);
      const color = label?.colour || '#000000';
      const deviceId = points[0]?.DeviceName || 'Unknown Device';
      const device = this.devices()?.find((d) => d.id.toString() === deviceId);
      const deviceName = device?.name || deviceId;
      const flaskName = points[0]?.FlaskName || key;

      // Track device for legend
      if (!seenDevices.has(deviceId)) {
        seenDevices.add(deviceId);
        const legendDevice: ILegendDevice = {
          deviceId,
          deviceName,
          flasks: [],
        };
        deviceMap.set(deviceId, legendDevice);
        legendItems.push(legendDevice);
      }

      const traceIndex = traces.length;

      // Add flask trace
      traces.push({
        x: points.map((p) => p.Time),
        y: points.map((p) => p.Value),
        mode: this.graphMode(),
        type: traceType,
        name: flaskName,
        marker: {
          color: color,
          size: markerSize,
        },
        line: {
          color: color,
          width: 1,
        },
        hovertemplate:
          '<b>Device:</b> %{customdata[0]}<br>' +
          '<b>Flask:</b> %{customdata[1]}<br>' +
          '<b>Time:</b> %{x}<br>' +
          '<b>Value:</b> %{y}' +
          (points[0]?.TargetOD
            ? '<br><b>Target OD:</b> %{customdata[2]}'
            : '') +
          '<extra></extra>',
        customdata: points.map((p) => [p.DeviceName, p.FlaskName, p.TargetOD]),
      } as Data);

      // Add to legend data
      deviceMap.get(deviceId)?.flasks.push({
        name: flaskName,
        colour: color,
        visible: true,
        traceIndex,
      });
    });

    const layout: Partial<Layout> = {
      // In Plotly, width/height include margins (unlike D3 where they're the plot area)
      width: this.width + this.margin.left + this.margin.right,
      height: this.height + this.margin.top + this.margin.bottom,
      xaxis: this.getXAxisConfig(),
      yaxis: this.getYAxisConfig(),
      showlegend: false,
      margin: {
        l: this.margin.left,
        r: this.margin.right,
        t: this.margin.top,
        b: this.margin.bottom,
      },
      hoverlabel: {
        bgcolor: '#B4DFFF',
        bordercolor: '#0C7BB4',
        font: {
          family: 'figtree',
          size: 13,
        },
      },
      dragmode: 'zoom',
      // Optimise hover for large datasets
      hovermode: totalPoints > 50000 ? false : 'closest',
    };

    const config: Partial<Config> = {
      scrollZoom: true,
      displayModeBar: false,
      displaylogo: false,
      responsive: true,
      // Performance optimisation for large datasets
      doubleClick: 'reset',
      // Reduce animation frames for better performance with large data
      staticPlot: false,
    };

    // Update legend data signal
    this.legendData.set(legendItems);

    try {
      Plotly.newPlot(this.plotlyDiv().nativeElement, traces, layout, config);
      this.graphReady = true;
      this.errorMessage = null;

      // Store initial axis ranges for zoom scale calculation
      const initialLayout = this.plotlyDiv().nativeElement.layout;
      this.initialXRange = initialLayout.xaxis?.range ? [...initialLayout.xaxis.range] as [number, number] : null;
      this.initialYRange = initialLayout.yaxis?.range ? [...initialLayout.yaxis.range] as [number, number] : null;

      // Handle layout changes (pan, zoom, etc.)
      this.plotlyDiv().nativeElement.on('plotly_relayout', () => {
        this.clearTooltips();
        this.updateMarkerSizeOnZoom();
      });
    } catch (error) {
      this.graphReady = false;
      this.errorMessage = 'Failed to render graph. Please try refreshing the page.';
    }
  };

  private updateGraph(): void {
    if (!this.plotlyDiv()?.nativeElement) return;

    // Group data by DeviceFlaskProperty
    const groupedData = new Map<string, IRenderData[]>();
    this.dataToRender.forEach((point) => {
      if (!groupedData.has(point.DeviceFlaskProperty)) {
        groupedData.set(point.DeviceFlaskProperty, []);
      }
      groupedData.get(point.DeviceFlaskProperty)!.push(point);
    });

    // Determine if we should use WebGL based on total point count
    const totalPoints = this.dataToRender.length;
    const useWebGL = totalPoints > 5000; // Threshold: 5k points
    const traceType = useWebGL ? 'scattergl' : 'scatter';

    // Optimise marker size for very large datasets and update base for zoom scaling
    const markerSize = totalPoints > 50000 ? 3 : totalPoints > 10000 ? 4 : 6;
    this.baseMarkerSize = markerSize;

    // Create traces and rebuild custom legend data
    const seenDevices = new Set<string>();
    const traces: Data[] = [];
    const legendItems: ILegendDevice[] = [];
    const deviceMap = new Map<string, ILegendDevice>();

    Array.from(groupedData.entries()).forEach(([key, points]) => {
      const label = this.labelList.find((l) => l.deviceFlaskProperty === key);
      const color = label?.colour || '#000000';
      const deviceId = points[0]?.DeviceName || 'Unknown Device';
      const device = this.devices()?.find((d) => d.id.toString() === deviceId);
      const deviceName = device?.name || deviceId;
      const flaskName = points[0]?.FlaskName || key;

      // Track device for legend
      if (!seenDevices.has(deviceId)) {
        seenDevices.add(deviceId);
        const legendDevice: ILegendDevice = {
          deviceId,
          deviceName,
          flasks: [],
        };
        deviceMap.set(deviceId, legendDevice);
        legendItems.push(legendDevice);
      }

      const traceIndex = traces.length;

      // Add flask trace
      traces.push({
        x: points.map((p) => p.Time),
        y: points.map((p) => p.Value),
        mode: this.graphMode(),
        type: traceType,
        name: flaskName,
        marker: {
          color: color,
          size: markerSize,
        },
        line: {
          color: color,
          width: 1,
        },
        hoverlabel: {
          bgcolor: '#B4DFFF',
          bordercolor: '#0C7BB4',
          font: {
            family: 'figtree',
            size: 13,
          },
        },
        hovertemplate:
          '<b>Device:</b> %{customdata[0]}<br>' +
          '<b>Flask:</b> %{customdata[1]}<br>' +
          '<b>Time:</b> %{x}<br>' +
          '<b>Value:</b> %{y}' +
          (points[0]?.TargetOD
            ? '<br><b>Target OD:</b> %{customdata[2]}'
            : '') +
          '<extra></extra>',
        customdata: points.map((p) => [p.DeviceName, p.FlaskName, p.TargetOD]),
      } as Data);

      // Add to legend data
      deviceMap.get(deviceId)?.flasks.push({
        name: flaskName,
        colour: color,
        visible: true,
        traceIndex,
      });
    });

    const layout: Partial<Layout> = {
      xaxis: this.getXAxisConfig(),
      yaxis: this.getYAxisConfig(),
      showlegend: false,
      margin: {
        l: this.margin.left,
        r: this.margin.right,
        t: this.margin.top,
        b: this.margin.bottom,
      },
    };

    // Update legend data signal
    this.legendData.set(legendItems);

    try {
      Plotly.react(this.plotlyDiv().nativeElement, traces, layout);
      this.errorMessage = null;
    } catch (error) {
      this.errorMessage = 'Failed to update graph with new data. Please try refreshing.';
    }
  }

  toggleLegendItem(deviceIndex: number, flaskIndex: number): void {
    this.legendData.update((items) => {
      const updatedItems = [...items];
      updatedItems[deviceIndex] = {
        ...updatedItems[deviceIndex],
        flasks: updatedItems[deviceIndex].flasks.map((flask, idx) =>
          idx === flaskIndex ? { ...flask, visible: !flask.visible } : flask,
        ),
      };
      return updatedItems;
    });

    const flask = this.legendData()[deviceIndex].flasks[flaskIndex];

    this.safePlotlyOperation(
      () => Plotly.restyle(
        this.plotlyDiv().nativeElement,
        {
          visible: flask.visible ? true : 'legendonly',
        },
        [flask.traceIndex],
      ),
      'Failed to toggle legend item. Please try again.'
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.graphReady = false;
    if (this.plotlyDiv()?.nativeElement) {
      try {
        Plotly.purge(this.plotlyDiv().nativeElement);
      } catch (error) {
        // Silently fail on cleanup - component is being destroyed anyway
      }
    }
  }
}

export interface IRenderData {
  DeviceFlaskProperty: string;
  Time: number;
  Value: number;
  DeviceName: string;
  FlaskName: string;
  TargetOD?: number;
}

export interface IPropertyColour {
  deviceFlaskProperty: string;
  colour: string;
}

export interface ILegendFlask {
  name: string;
  colour: string;
  visible: boolean;
  traceIndex: number;
}

export interface ILegendDevice {
  deviceId: string;
  deviceName: string;
  flasks: ILegendFlask[];
}
